import { useQuery } from "@hooks/useQuery";
import { Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useNotification } from "~/Hooks/useNotification";
import { useDocumentDetails } from "~/Modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { fields } from "../constants";
import { useTermsAndConditions } from "../Pages.hook";
import { Faqs } from "./Faqs/Faqs";

export const EramanPrivilegeCard = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const {
    data: termsAndConditionsData,
    isLoading: isLoadingTermsAndConditions,
    refetch: refetchTermsAndConditions,
  } = useTermsAndConditions();
  const {
    data: pagesDataById,
    isLoading: isLoadingPagesById,
    refetch: refetchData,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("subActiveTab") === "TermsAndConditions") {
      refetchTermsAndConditions();
    }
  }, [query.get("subActiveTab"), query.get("activeTab")]);

  useEffect(() => {
    refetchData();
  }, [query?.get("id")]);

  const TermsAndConditionsFormConfig: FormConfig = {
    initialValues: {
      field_page_title_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_page_title_en)
        : decodeHTML(termsAndConditionsData?.[0]?.field_page_title_en || ""),

      field_page_title_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_page_title_ms)
        : decodeHTML(termsAndConditionsData?.[0]?.field_page_title_ms || ""),

      field_page_title_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_page_title_zh)
        : decodeHTML(termsAndConditionsData?.[0]?.field_page_title_zh || ""),

      field_content_description_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_content_description_en)
        : decodeHTML(termsAndConditionsData?.[0]?.field_content_description_en || ""),

      field_content_description_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_content_description_ms)
        : decodeHTML(termsAndConditionsData?.[0]?.field_content_description_ms || ""),

      field_content_description_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById.payload.field_content_description_zh)
        : decodeHTML(termsAndConditionsData?.[0]?.field_content_description_zh || ""),
    },
    fields: fields.TermsAndConditions,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["newsImage"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: "eramanPrivilegeCard_TermsAndConditions",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        id: termsAndConditionsData?.[0]?.id || pagesDataById?.payload?.id,
        ...data,
        published: true,
      },
      status: status,
    };
    console.log(payload, "payload");
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const createSubItems = (label: string, key: string, formConfig: FormConfig) => ({
    label,
    key,
    children: (
      <DynamicForm
        isLoading={isLoadingPagesById || isLoadingTermsAndConditions}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${eramanHost}/api/page-headers-data-list-crud-api/${termsAndConditionsData?.[0]?.id || pagesDataById?.payload?.id}`,
            "UPDATE",
            "PATCH",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    ),
  });

  const eramanPrivilegeCard = [
    createSubItems("Terms & Conditions", "TermsAndConditions", TermsAndConditionsFormConfig),
    {
      label: "FAQs",
      key: "Faqs",
      children: <Faqs />,
    },
  ];

  return (
    <Tabs
      activeKey={query?.get("subActiveTab") || ""}
      items={eramanPrivilegeCard}
      onChange={(activeKey) => {
        navigate(`${window.location.pathname}?activeTab=${query.get("activeTab")}&subActiveTab=${activeKey}`);
      }}
    ></Tabs>
  );
};
